/**
 * Progress notes — the section's presentation constants, plus the composer's
 * sample draft. Colours and the composer prefill are transcribed from the Figma
 * "Progress notes" flow board (feed 2156:3257, MEDICAL_RECORDS-filtered variant
 * 2159:3257, composer filled 2162:3257 / empty 2167:3257).
 *
 * The feed itself is live — notes come from the progress_notes API via
 * PatientProgressNotesService and progressNoteFormatters.toCard. Nothing here is
 * note data.
 *
 * CATEGORIES drives the feed filter-chip row (leading "ALL" = show everything)
 * and the composer category tabs (which drop the "ALL" pseudo-category). tagColor
 * returns the per-category chip pair: the three sampled on real cards (BD,
 * CALL_UPDATE, MEDICAL_RECORDS) are the exact Figma hexes; the rest are
 * palette-consistent fills for the categories that only ever show as filter
 * chips, so a card in any category still renders a sensible tag.
 */

import { TAGS } from "../../helper/progressNoteFormatters";

/**
 * Full category set — order matters (it is the chip / tab order), and it happens
 * to be the tag table's own order. Derived rather than re-typed so a chip can
 * never name a tag the API doesn't have: tagValue() has to resolve every one of
 * these to an int for the filtered fetch.
 */
export const CATEGORIES = TAGS.map((t) => t.name);

/**
 * Per-category tag-chip colours { bg, fg }. Exact Figma hex for the three that
 * appear on the sampled cards; palette-consistent pastels for the filter-only
 * categories.
 */
export const TAG_COLORS = {
  CALL_UPDATE: { bg: "#dbeafe", fg: "#1d4ed8" },
  MEDICAL_RECORDS: { bg: "#ede9fe", fg: "#6d28d9" },
  BD: { bg: "#fef3c7", fg: "#92400e" },
  NUTRITION: { bg: "#dcfce7", fg: "#15803d" },
  CM_CHAT: { bg: "#cffafe", fg: "#0e7490" },
  MIN: { bg: "#f1f5f9", fg: "#475569" },
  ESCALATION: { bg: "#fee2e2", fg: "#b42318" },
  PHYSIOTHERAPY: { bg: "#fce7f3", fg: "#be185d" },
  DOCTOR_NOTES: { bg: "#e0e7ff", fg: "#4338ca" },
};

/** Falls back to the neutral idle-chip fill for an unmapped category. */
export const tagColor = (category) =>
  TAG_COLORS[category] || { bg: "#f4f5f7", fg: "#4a5565" };

/**
 * Prefill for the composer's filled variant (2162:3257): the selected category
 * tab, the typed note body and its single task.
 */
export const COMPOSER_SAMPLE = {
  category: "CALL_UPDATE",
  note:
    "Following up with the patient on their blocked-nose symptoms discussed on the last call. Advised steam inhalation and saline drops; will reassess in 3 days.",
  task: {
    description: "Follow up on blocked-nose symptoms in 3 days",
    project: "Follow up",
    assignTo: "Trishla Rautela",
    dueDate: "09/07/2026",
    escalateToSlack: false,
  },
};
